import { Table, Button, Space, notification, Popover, Spin, Tooltip, Row, Col, Flex } from "antd";
import type { TableProps } from "antd";
import { TOKEN_KEY } from "../../../authProvider";
import {
    DateField,
    TextField,
    List,
    DeleteButton,
    EditButton,
    ShowButton,
} from "@refinedev/antd";
import { useUpdate, HttpError, useCustomMutation, useNotification } from "@refinedev/core";
import { format, formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BanModal from "./BanModal";
import MarkReviewed from "./MarkReviewedButton";
import { getEntityType } from "./getEntityType";
import UnbanButton from "./UnbanButton";
import { DeleteOutlined } from "@ant-design/icons";



export const ReportReviewList = ({ tableProps, showReportType = false, showReviewdBy = false, actionButtonsToShow = {
    markAsReviewed: false,
    globalBan: false,
    channelBan: false,
    channelBan24H: false,
    globalBan24H: false,
    unBan: false,
    delete: false,
    showButton: false,
} }:
    {
        tableProps: TableProps<any>, showReportType?: boolean, showReviewdBy?: boolean, actionButtonsToShow?: {
            markAsReviewed?: boolean,
            globalBan?: boolean,
            channelBan?: boolean,
            channelBan24H?: boolean,
            globalBan24H?: boolean,
            unBan?: boolean,
            delete?: boolean,
            showButton?: boolean,
        }
    }) => {
    const { mutate, isLoading, variables } = useCustomMutation();
    const navigate = useNavigate();
    const { open: notify } = useNotification();
    const token = localStorage.getItem(TOKEN_KEY);
    const apiUrl = import.meta.env.VITE_API_URL;


    const handleUnban = async (itemID: string, userID: string) => {
        try {
            mutate(
                {
                  url: `${apiUrl}/admin/report/submit_action`,
                  method: 'post',
                  config: {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    },
                  },
                  values: {
                    actionType: "unban",
                    itemID,
                    userID,
                    unban: {},
                  },
                },
                {
                  onSuccess: (data: any) => {
                    notify!({
                      type: "success",
                      message: "User unbanned successfully",
                      description: data?.message ,
                    });
                  },
                  onError: (error: any) => {
                    notify!({
                      type: "error",
                      message: "Failed to submit ban",
                      description: error?.message,
                    });
                  }
                }
            );  
        } catch (error) {
            console.log("Error unbanning user", error);
        }
    };

    const handleMarkReviewed = (itemID: string, userID: string) => {
        const token = localStorage.getItem(TOKEN_KEY);
        const apiUrl = import.meta.env.VITE_API_URL;
        try {
            mutate(
                {
                  url: `${apiUrl}/admin/report/submit_action`,
                  method: 'post',
                  config: {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    },
                  },
                  values: {
                    actionType: "mark_reviewed",
                    itemID,
                    userID,
                  },
                },
                {
                  onSuccess: (data: any) => {
                    notify!({
                      type: "success",
                      message: "Content marked 'reviewed' successfully",
                      description: data?.message ,
                    });
                  },
                  onError: (error: any) => {
                    notify!({
                      type: "error",
                      message: "Failed to submit review",
                      description: error?.message,
                    });
                  }
                }
            );  
        } catch (error) {
            console.log("Error unbanning user", error);
        }
    }

    const handleDelete = async (itemID: string, userID: string) => {
        try {
            mutate(
                {
                    url: `${apiUrl}/admin/report/submit_action`,
                    method: 'post',
                    config: {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                    },
                    values: {
                        actionType: "delete_message",
                        itemID,
                        userID,
                    },
                },
                {
                    onSuccess: (data: any) => {
                        notify!({
                            type: "success",
                            message: "Content deleted successfully",
                            description: data?.message,
                        });
                    },
                    onError: (error: any) => {
                        notify!({
                            type: "error",
                            message: "Failed to delete content",
                            description: error?.message,
                        });
                    }
                }
            );
        } catch (error) {
            console.log("Error deleting content", error);
        }
    };

    // console.log("Data", tableProps?.dataSource);
    // console.log("Variables", variables);


    return (
        <List>
            <Table
                rowKey="id"
                {...tableProps}
                pagination={{
                    ...tableProps.pagination,
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} reports`,
                    position: ['bottomCenter']
                }}
            >
                <Table.Column
                    dataIndex="updatedAt"
                    title={
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            Last reported date
                        </div>
                    }
                    render={(value, record) => (
                        <div>
                            <Popover content={format(value, "MM/dd/yy hh:mm a")}>
                                <span>
                                    {formatDistanceToNow(value)} ago
                                </span>
                            </Popover>
                        </div>
                    )}
                />
                <Table.Column
                    title="Reported User"
                    render={(_: any, record: ReportRecord) => {
                        return (
                            <TextField value={record.entityCreator.name || 'N/A'}
                                onClick={() => {
                                    navigate(`/users/${record.entityCreator.id}`);
                                }}
                                style={{
                                    cursor: 'pointer',
                                    textDecoration: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                }}
                            />
                        )
                    }}
                />

                <Table.Column
                    title="Reporter"
                    render={(value, record: ReportRecord) => {
                        return (
                            <TextField value={'AutoMod'} />
                        )
                    }}
                />

                <Table.Column
                    title="Reason"
                    render={(value, record: ReportRecord) => (
                        <Tooltip title={record.flags[0]?.reason || '-'}>
                            <TextField value={record.flags[0]?.reason ? `${record.flags[0].reason.slice(0, 50)}${record.flags[0].reason.length > 50 ? '...' : ''}` : '-'} />
                        </Tooltip>
                    )}
                />

                {showReportType && <Table.Column
                    dataIndex="entityType"
                    title="Type"
                    render={(value: string) => (
                        <TextField value={getEntityType(value)} />
                    )}
                />}


                {showReviewdBy && <Table.Column
                    dataIndex="reviewedByUser"
                    title="Reviewed By"
                    render={(_: unknown, record: ReportRecord) => {
                        return (
                            <div>
                                <Popover content={record.reviewedByUser?.username}>
                                    <span>
                                        <TextField value={record.reviewedByUser?.fullName || 'N/A'}
                                            onClick={() => {
                                                navigate(`/users/${record.reviewedByUser?.id}`);
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                textDecoration: 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.textDecoration = 'underline';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.textDecoration = 'none';
                                            }}
                                        />
                                    </span>
                                </Popover>
                            </div>

                        )
                    }}
                />}


                <Table.Column
                    title={"Actions"}
                    width="20%"
                    dataIndex="actions"
                    render={(_, record: ReportRecord) => {
                        const isUserBanned = record?.entityCreator.banned;
                        const isContentBanned = record?.bans.length;
                        const isEntityDeleted = record.message?.type === 'deleted';

                        return (
                            <Flex gap={12} wrap="wrap">
                                <Tooltip title="View Details">
                                    {actionButtonsToShow.showButton && <ShowButton hideText recordItemId={record?.id} />}
                                </Tooltip>
                                {actionButtonsToShow.markAsReviewed && <MarkReviewed
                                    itemID={record.id}
                                    userID={record.entityCreator.id}
                                    handleClick={(itemID, userID) => handleMarkReviewed(itemID, userID)}
                                    isLoading={(variables?.values as any)?.itemID === record.id ? isLoading : false}
                                />}
                                {!isUserBanned && (<>
                                    {actionButtonsToShow.globalBan && <BanModal itemID={record.id} is24H={false} channelBanOnly={false} userID={record?.entityCreator.id}/>}
                                    {actionButtonsToShow.globalBan24H && <BanModal itemID={record.id} is24H={true} channelBanOnly={false} userID={record?.entityCreator.id}  />}

                                    {!isContentBanned && actionButtonsToShow.channelBan && <BanModal itemID={record.id} is24H={false} channelBanOnly={true} userID={record?.entityCreator.id} />}
                                    {!isContentBanned && actionButtonsToShow.channelBan24H && <BanModal itemID={record.id} is24H={true} channelBanOnly={true} userID={record?.entityCreator.id}  />}
                                </>)}
                                {isUserBanned && <UnbanButton handleClick={() => handleUnban(record.id, record?.entityCreator.id)} />}
                                {(!isEntityDeleted && actionButtonsToShow.delete) && (
                                    <Tooltip title="Delete">
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete(record.id, record.entityCreator.id)}
                                            loading={(variables?.values as any)?.itemID === record.id ? isLoading : false}
                                        />
                                    </Tooltip>
                                )}
                            </Flex>
                        )
                    }}
                />
            </Table>
        </List>
    );
};